import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

import { AuthCaptcha } from "../styles";

import { LOGIN_BACKEND_URL, attemptAuth } from "../helpers";
import { requestLoginCode, confirmLoginCode, clearLoginCode } from "./helpers";

const Context = createContext();

export const VIEWS = {
    DEFAULT: "default",
    PASSWORD_LOGIN: "password-login",
    REQUEST_CODE: "request-code",
    CONFIRM_CODE: "confirm-code",
};

export const LoginFlowProvider = ({
    children,
    afterAuth = () => {},
    goToPasswordReset = () => {},
    requireCaptcha,
    hasSocial = true,
    resetPasswordIsExternal = false,
}) => {
    const [view, setView] = useState(VIEWS.DEFAULT);
    const [loginCodeRequested, setLoginCodeRequested] = useState(false);

    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [captchaToken, setCaptchaToken] = useState(null);
    const [loginCode, setLoginCode] = useState("");

    const [isProcessing, setIsProcessing] = useState(false);
    const [backendError, setBackendError] = useState("");

    useEffect(() => {
        setBackendError("");
    }, [view]);

    const onRequestFail = errorMsg => {
        setIsProcessing(false);
        setBackendError(errorMsg);
    };

    const goToDefaultView = () => setView(VIEWS.DEFAULT);
    const goToPasswordLoginView = () => setView(VIEWS.PASSWORD_LOGIN);
    const goToCodeRequestView = () => setView(VIEWS.REQUEST_CODE);

    const startLoginWithPassword = async () => {
        setIsProcessing(true);

        if (loginCodeRequested) {
            await clearLoginCode();
            setLoginCodeRequested(false);
        }

        attemptAuth({
            url: LOGIN_BACKEND_URL,
            form: {
                login: emailAddress,
                password,
                captcha_token: captchaToken,
            },
            onSuccess() {
                setIsProcessing(false);
                afterAuth();
            },
            onFail(errorMsg) {
                onRequestFail(errorMsg);
            },
        });
    };

    const startLoginCodeRequest = () => {
        setIsProcessing(true);

        requestLoginCode({
            emailAddress,
            onSuccess() {
                setLoginCodeRequested(true);
                setIsProcessing(false);
                setView(VIEWS.CONFIRM_CODE);
            },
            onFail(errorMsg) {
                onRequestFail(errorMsg);
            },
        });
    };

    const startLoginCodeConfirmation = () => {
        setIsProcessing(true);

        confirmLoginCode({
            loginCode,
            onSuccess() {
                setIsProcessing(false);
                afterAuth();
            },
            onFail(errorMsg) {
                onRequestFail(errorMsg);
            },
        });
    };

    const captcha = requireCaptcha && (
        <AuthCaptcha handleToken={setCaptchaToken} />
    );

    return (
        <Context.Provider
            value={{
                view,
                loginCodeRequested,
                // inputs
                emailAddress,
                setEmailAddress,
                password,
                setPassword,
                loginCode,
                setLoginCode,
                captcha,
                captchaIsValid: !requireCaptcha || captchaToken,
                // navigation
                goToCodeRequestView,
                goToPasswordLoginView,
                goToDefaultView,
                goToPasswordReset,
                // login-related actions
                startLoginWithPassword,
                startLoginCodeRequest,
                startLoginCodeConfirmation,
                // state
                isProcessing,
                backendError,
                hasSocial,
                resetPasswordIsExternal,
            }}
        >
            {children}
        </Context.Provider>
    );
};

LoginFlowProvider.propTypes = {
    afterAuth: PropTypes.func.isRequired,
    goToPasswordReset: PropTypes.func.isRequired,
    requireCaptcha: PropTypes.bool.isRequired,
    hasSocial: PropTypes.bool,
    resetPasswordIsExternal: PropTypes.bool,
};

export const useLoginFlow = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error("useLoginFlow must be used within a LoginFlowProvider");
    }

    return context;
};
